import { flexRender, Row, Table as TanStackTable } from '@tanstack/react-table'
import { useVirtualizer } from '@tanstack/react-virtual'
import { Box, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { useEffect, useRef } from 'react'

type InfiniteScrollTableProps<T> = {
  table: TanStackTable<T>
  loading: boolean
  hasMore: boolean
  loadMore: () => void
  onRowClick?: (row: Row<T>) => void
  emptyMessage?: string
  estimateRowSize?: number
  containerTestId?: string
}

const InfiniteScrollTable = <T,>({
  table,
  loading,
  hasMore,
  loadMore,
  onRowClick,
  emptyMessage = 'No results',
  estimateRowSize = 45,
  containerTestId,
}: InfiniteScrollTableProps<T>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { rows } = table.getRowModel()

  const rowVirtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateRowSize,
    overscan: 5,
  })

  const virtualRows = rowVirtualizer.getVirtualItems()
  const lastVirtualRowIndex = virtualRows[virtualRows.length - 1]?.index ?? -1

  useEffect(() => {
    if (lastVirtualRowIndex >= rows.length - 1 && hasMore && !loading) loadMore()
  }, [hasMore, loading, rows.length, lastVirtualRowIndex, loadMore])

  return (
    <Box
      ref={containerRef}
      data-testid={containerTestId}
      sx={{
        flex: 1,
        overflow: 'auto',
      }}
    >
      <Table size="small" sx={{ tableLayout: 'fixed', minWidth: 'max-content', display: 'grid' }}>
        <TableHead
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            display: 'grid',
          }}
        >
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow
              key={headerGroup.id}
              sx={{ display: 'flex', backgroundColor: 'background.paper', minWidth: '100%' }}
            >
              {headerGroup.headers.map((header, index) => (
                <TableCell
                  key={header.id}
                  sx={{
                    width: header.getSize(),
                    minWidth: header.column.columnDef.minSize ?? header.getSize(),
                    flexGrow: index === headerGroup.headers.length - 1 ? 1 : 0,
                    position: 'relative',
                    backgroundColor: 'inherit',
                    fontWeight: 'bold',
                    py: 1,
                    display: 'flex',
                    alignItems: 'center',
                    overflow: 'hidden',
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  {header.column.getCanResize() && (
                    <Box
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                      sx={{
                        position: 'absolute',
                        right: 1,
                        top: '25%',
                        height: '50%',
                        width: 2,
                        cursor: 'col-resize',
                        userSelect: 'none',
                        touchAction: 'none',
                        borderRadius: 0.5,
                        backgroundColor: header.column.getIsResizing() ? 'primary.main' : 'divider',
                        '&:hover': {
                          backgroundColor: 'primary.main',
                        },
                      }}
                    />
                  )}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableHead>
        <TableBody
          sx={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: 'relative',
            display: 'grid',
          }}
        >
          {rows.length === 0 && !loading ? (
            <TableRow sx={{ display: 'flex', width: '100%' }}>
              <TableCell sx={{ flex: 1, textAlign: 'center', border: 'none' }}>
                <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                  {emptyMessage}
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            virtualRows.map((virtualRow) => {
              const row = rows[virtualRow.index]
              return (
                <TableRow
                  key={row.id}
                  data-index={virtualRow.index}
                  ref={rowVirtualizer.measureElement}
                  onClick={onRowClick ? () => onRowClick(row) : undefined}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    transform: `translateY(${virtualRow.start}px)`,
                    display: 'flex',
                    '&:hover': onRowClick ? { backgroundColor: 'action.hover' } : undefined,
                  }}
                >
                  {row.getVisibleCells().map((cell, index) => (
                    <TableCell
                      key={cell.id}
                      sx={{
                        width: cell.column.getSize(),
                        flexGrow: index === row.getVisibleCells().length - 1 ? 1 : 0,
                        minWidth: 0,
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              )
            })
          )}
        </TableBody>
      </Table>
      {loading && (
        <Box
          sx={{
            py: 2,
            position: 'sticky',
            left: 0,
            width: containerRef.current?.clientWidth ?? '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
          }}
        >
          <CircularProgress size={20} />
          <Typography variant="body2" color="text.secondary">
            Loading…
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default InfiniteScrollTable
